import type {CanonicalGameFrame} from '../proofs/uses/canonicalGameSimulation';
import {FREELANCE_SCORE} from '../narrative/freelanceScore';
import {SHARE_SEQUENCE} from '../proofs/share/sequence';
import type {SoundCue,SoundPosition} from './voices';

export interface LampSoundFrame {
 content:number;mode:string;local:number;airborne:number;x:number;squash:number;beam:number;position?:SoundPosition;
}
export interface SoundFrame {
 chapter:string;visit:number;phase:string;elapsed:number;clock:number;transition:number;
 paused:boolean;hidden:boolean;reduced:boolean;p:number;roll:number;pan:number;
 game?:CanonicalGameFrame|null;lamp?:LampSoundFrame|null;
 position?:SoundPosition;
 typing?:Array<{id:string;length:number;pressed:boolean;pan:number;automatic?:boolean}>;
 opening?:{elapsed:number;settledAt:number};
 trailerTime?:number|null;
}
/** Detect boundaries on the actor clocks, never replay a backlog after a seek.
 * Sampling while muted is intentional: enabling sound cannot replay old hits. */
export class SoundScore {
 private scope='';
 private cursors=new Map<string,number>();
 private previous:SoundFrame|null=null;
 private lastContact=-Infinity;
 private lastAir=-Infinity;
 private cues:SoundCue[]=[];
 private crossed(key:string,value:number,marks:readonly number[],cue:SoundCue,maxStep=1200){
  const previous=this.cursors.get(key);this.cursors.set(key,value);
  if(previous===undefined||value<previous||value-previous>maxStep)return;
  const mark=marks.find(mark=>previous<mark&&value>=mark);
  if(mark!==undefined)this.cues.push((cue.sample||cue.sustain)&&maxStep>=1000&&value>mark?{...cue,elapsed:(value-mark)/1000}:cue);
 }
 sample(f:SoundFrame):SoundCue[]{
  this.cues=[];const scope=`${f.visit}:${f.chapter}`;
  if(scope!==this.scope){this.scope=scope;this.cursors.clear();this.previous=null;}
  const previous=this.previous;this.previous=f;
  // Register the new pose on resume without inventing motion during a pause.
  if(f.paused||f.hidden||f.reduced){this.cursors.clear();return[];}
  const pan=Math.max(-.65,Math.min(.65,f.pan));
  for(const input of f.typing??[]){
   const old=previous?.typing?.find(item=>item.id===input.id),step=input.length-(old?.length??input.length);
   if(old&&step>0&&step<=5&&previous&&f.clock-previous.clock<250)this.cues.push({kind:'tap',sample:input.length%3===0?'foley_key1':input.length%3===1?'foley_key2':'foley_key3',gain:f.typing!.length>1?.25:.32,pan:input.pan});
   if(old&&!old.pressed&&input.pressed&&!input.automatic&&previous&&f.clock-previous.clock<250)this.cues.push({kind:'tap',sample:'foley_mouse',gain:.95,pan:input.pan});
  }
  if(f.phase==='transition'){
   // Chapter boundaries add no artificial noise or contact accents.
   return this.spatialize(f);
  }
  if(f.phase==='hero'){
   if(f.opening){
    if(!previous&&f.opening.elapsed<=250)this.cursors.set('opening-arrival',0);
    this.crossed('opening-air',f.opening.elapsed,[30],{kind:'air',gain:.5,duration:3.2});
    this.crossed('opening-arrival',f.opening.elapsed,[30],{kind:'title',sample:'logo_ORIGINAL_18s',gain:.32,offset:14.345714-(f.opening.settledAt-30)/1000,fadeIn:.35,fadeOut:1.1});
   }
   return this.cues;
  }
  if(f.chapter==='film'){
   if(f.trailerTime!=null){
    const ms=f.trailerTime*1000;
    // At ordinary entry the first presented frame can already be 30ms into the
    // trailer. A seek into its middle must still remain silent.
    if(ms<=150&&previous?.trailerTime==null&&!previous?.paused&&!previous?.hidden&&!previous?.reduced&&!this.cursors.has('trailer-bed'))this.cursors.set('trailer-bed',0);
    // Spot against the actual reproduced frame, not an estimate of scroll time.
    // Trailer stems use the accepted finale's reference level (.32): 0.2667
    // of the original 1.2 effective stem gain. Preserve the authored envelopes. The rising
    // L_planeup_2 transformation is intentionally absent: this cube is formed.
    this.crossed('trailer-bed',ms,[20],{kind:'title',sample:'logo_ambient_89',gain:.17,offset:.72,clipDuration:14.5,fadeIn:2.4,fadeOut:3.6});
    this.crossed('trailer-empty',ms,[600],{kind:'title',sample:'a01_dead_v1',gain:.32,clipDuration:7.08,fadeIn:1.1,fadeOut:1.3});
    this.crossed('trailer-open',ms,[2100],{kind:'title',sample:'a03_open_v1',gain:.32,clipDuration:11.06,fadeIn:.7,fadeOut:1.8});
    this.crossed('trailer-light',ms,[6300],{kind:'title',sample:'syn_glass_ping',gain:.17,fadeIn:.55,fadeOut:.12});
   }
   this.crossed('axes-off',f.p,[.9920425],{kind:'title',sample:'L_off_2',gain:.32,clipDuration:8,fadeIn:.05,fadeOut:.2},.035);
   this.crossed('run-click',f.p,[.578],{kind:'tap',sample:'foley_mouse',gain:.95},.035);
   this.crossed('run',f.p,[.578],{kind:'start',delay:.055},.035);
   this.crossed('first-hop',f.p,[.658],{kind:'air',gain:.45,pan,duration:1.3},.02);
   this.crossed('bridge-flight',f.p,[.752],{kind:'air',gain:.65,duration:2.2,pan},.02);
   this.crossed('blender-flight',f.p,[.942],{kind:'air',gain:.6,duration:1.8,pan},.02);
   this.crossed('exit-flight',f.p,[.999175],{kind:'air',gain:.65,duration:1.35,pan},.02);
   this.crossed('lands',f.p,[.688,.855],{kind:'cube',gain:.7,pan},.02);
   const rolling=f.p>=.688&&f.p<.752||f.p>=.855&&f.p<.942;
   if(rolling&&previous&&f.clock-this.lastContact>180&&Math.floor(f.roll/(Math.PI*.5))!==Math.floor(previous.roll/(Math.PI*.5))&&Math.abs(f.roll-previous.roll)<Math.PI*.6){this.cues.push({kind:'cube',gain:.09,pan});this.lastContact=f.clock;}
  }else{
   if(f.chapter==='uses-freelance'){
    for(const[key,pitch]of [['towers',.75],['deck',1],['cables',1.25]] as const)this.crossed(key,f.elapsed,[FREELANCE_SCORE[key]],{kind:'assemble',gain:1.05,pitch,pan:-.22});
    this.crossed('material',f.elapsed,[FREELANCE_SCORE.materials],{kind:'title',gain:.65,pan:-.22});
    this.crossed('arches',f.elapsed,[FREELANCE_SCORE.flight],{kind:'air',gain:.58,duration:(FREELANCE_SCORE.end-FREELANCE_SCORE.flight)/1000,sustain:true,pan});
   }
   if(f.chapter==='pricing'){
    for(const [i,at] of [4300,5900,7500,9100].entries())this.crossed(`plan-${i}`,f.elapsed,[at],{kind:'assemble',gain:1.05,pitch:[.75,1,1.25,1.5][i],pan:-.45+i*.3});
   }
   if(f.chapter==='share'){
    this.crossed('photo-jump',f.elapsed,[SHARE_SEQUENCE.openingEnd],{kind:'air',gain:.65,pan:.35,duration:1.1});
    this.crossed('photo',f.elapsed,[SHARE_SEQUENCE.settlingEnd+25],{kind:'shutter',sample:'foley_camera',gain:.58,pan:.3});
    // These are demonstrated hovers, not real presses: deliberately silent.
   }
   if(f.chapter==='closer'){
    this.crossed('closer-one',f.elapsed,[180],{kind:'title',sample:'a03_open_v1',gain:.425,clipDuration:5.8,fadeIn:.7,fadeOut:2.1});
    this.crossed('closer-depth',f.elapsed,[4350],{kind:'title',sample:'a03_open_v1',gain:.3225,offset:2.1,clipDuration:7.4,fadeIn:1.6,fadeOut:2.2});
   }
   if(f.chapter==='footer'){
    this.crossed('world-tilt',f.elapsed,[300],{kind:'air',gain:.7,duration:2.2,pan:.2});
    this.crossed('home',f.elapsed,[30],{kind:'title',sample:'logo_ORIGINAL_18s',gain:.32,offset:6.375714,fadeIn:1.6,fadeOut:1.1});
   }
  }
  const game=f.game,old=previous?.game;
  if(game&&old&&game.elapsed>=old.elapsed&&game.elapsed-old.elapsed<.3){
   if(game.entry>=1&&game.exit===0){
    if(!old.grounded&&game.grounded)this.cues.push({kind:'cube',gain:game.slowMotion?.45:.7,pan:game.cubeX*.15,pitch:game.slowMotion?.82:1});
    if(old.grounded&&!game.grounded)this.cues.push({kind:'air',gain:.25,pan:game.cubeX*.15,duration:game.slowMotion?1:.5});
   }
   if(game.exit>0&&old.exit===0)this.cues.push({kind:'air',gain:.7,duration:1.5,pan:.25});
  }
  const lamp=f.lamp,oldLamp=previous?.lamp;
  if(lamp){
   const lp=Math.max(-.55,Math.min(.65,lamp.x*.13+.2));
   if(oldLamp&&lamp.content>=oldLamp.content&&lamp.content-oldLamp.content<.3){
    if(oldLamp.airborne>.004&&lamp.airborne<=.004&&!(lamp.mode==='physics'&&lamp.local>=5.6&&lamp.local<6))this.cues.push({kind:'spring',gain:lamp.content<5?.9:.7,pan:lp,position:lamp.position});
    if(oldLamp.airborne<=.004&&lamp.airborne>.004)this.cues.push({kind:'air',duration:.45,gain:.25,pan:lp,pitch:1.12,position:lamp.position});
   }
   const mode=`lamp-${lamp.mode}`;
   if(lamp.mode==='lighting')this.crossed(mode,lamp.local*1000,[900,4000,7100,10200,13300,16400],{kind:'switch',sample:'physical_switch',gain:.9,pan:lp,position:lamp.position});
   if(lamp.mode==='materials')this.crossed(mode,lamp.local*1000,[675,3225,5775,8325,10875,13425],{kind:'assemble',gain:.35,pan:lp});
   if(lamp.mode==='modeling')this.crossed(mode,lamp.local*1000,[2800,3800,5200],{kind:'assemble',gain:.25,pan:lp});
   if(lamp.mode==='physics'){
    this.crossed('blink',lamp.local*1000,[7000,7760],{kind:'surprise',gain:.65,pan:lp,position:lamp.position});
    this.crossed('squash',lamp.local*1000,[5650],{kind:'squash',pan:lp});
    this.crossed('recover',lamp.local*1000,[9900],{kind:'recover',pan:-.15});

   }
   // Optional epilogues follow actual airborne edges above. No second mode
   // duration table or periodic synthetic hit can drift from the animation.

  }
  return this.spatialize(f);
 }
 private spatialize(f:SoundFrame){return this.cues.map(cue=>['air','cube','spring','squash','recover'].includes(cue.kind)&&f.position?{...cue,position:cue.position??f.position,follow:cue.kind==='air'}:cue);}
 reset(){this.scope='';this.cursors.clear();this.previous=null;this.lastContact=-Infinity;}
}
